import { useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box, Divider, styled } from '@mui/material';

import {
  ReservationFormInput,
  ReservationFormValues,
} from '../schema/reservationForm.schema';

import {
  TransportationAttribute,
  TransportationAttributeGroup,
  TransportationAttributeSubgrouping,
} from '@/data/models/transportation-attribute.model';

import { usePortalContext } from '@/contexts/PortalProvider';

import Checkbox from '@/components/forms/Checkbox';
import SelectMenu from '@/components/forms/SelectMenu';
import {
  AttributeControlType,
  SecondaryAttribute,
} from '../schema/secondaryAttributes.model';

const PREFIX = 'AdditionalAttributes';

const classes = {
  subctrl: `${PREFIX}-subctrl`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  [`& .${classes.subctrl}`]: {
    marginLeft: theme.spacing(3),
  },
}));

type AdditionalAttributesProps = {
  form: UseFormReturn<ReservationFormInput, unknown, ReservationFormValues>;
  transportationAttributes: TransportationAttribute[];
};

const AdditionalAttributes = ({
  form,
  transportationAttributes,
}: AdditionalAttributesProps) => {
  const { t } = useTranslation('common');
  const requiredFieldMessage = t('common.requiredField');
  const { isPortal } = usePortalContext();

  const additionalAttributes = form.watch('AdditionalAttributes');

  const emptyAttribute: TransportationAttribute = useMemo(
    () => ({
      transportationAttributeCategoryBC: '',
      name: t('reservation.noAttribute'),
      description: '',
      isPassengerSelectable: true,
      isDefault: false,
    }),
    [t],
  );

  const otherAttributes = useMemo(() => {
    if (!transportationAttributes) return [];

    const otherAttrs: SecondaryAttribute[] = [];
    const secondary = transportationAttributes.filter(
      (a) =>
        a.grouping === TransportationAttributeGroup.SECONDARY &&
        a.isPassengerSelectable,
    );
    secondary.forEach((s) => {
      let options: TransportationAttribute[] | undefined = undefined;
      if (s.childTransportationAttributeCategoryBC) {
        const children = transportationAttributes.filter(
          (ta) =>
            ta.transportationAttributeCategoryBC ===
            s.childTransportationAttributeCategoryBC,
        );
        if (children?.length) {
          options = children.sort((c1, c2) => c1.name.localeCompare(c2.name));
        }
      }
      const sAttr: SecondaryAttribute = {
        id: s.transportationAttributeUUID!,
        name: s.name,
        controlType: options
          ? AttributeControlType.DROPDOWN
          : AttributeControlType.CHECKBOX,
        listOptions: options,
        displayOrder: s.displayOrder || 0,
      };
      if (
        s.subgrouping !== TransportationAttributeSubgrouping.ADDITIONALPASSENGER
      ) {
        otherAttrs.push(sAttr);
      }
    });
    otherAttrs.sort((a, b) => a.displayOrder - b.displayOrder);
    return otherAttrs;
  }, [transportationAttributes]);

  if (isPortal) return null;

  return (
    <StyledBox>
      <Divider />
      <Checkbox
        name={`AdditionalAttributes`}
        label={t('reservation.otherAttributes')}
        control={form.control as any}
      />
      {additionalAttributes && (
        <>
          {!!otherAttributes?.length &&
            otherAttributes.map((sa) => {
              if (sa.controlType === AttributeControlType.CHECKBOX) {
                return (
                  <Checkbox
                    key={sa.id}
                    className={classes.subctrl}
                    name={`SecondaryAttribute_${sa.id}`}
                    label={sa.name}
                    control={form.control as any}
                  />
                );
              } else if (
                sa.controlType === AttributeControlType.DROPDOWN &&
                sa.listOptions?.length
              ) {
                return (
                  <SelectMenu
                    key={sa.id}
                    className={classes.subctrl}
                    label={sa.name}
                    name={`SecondaryAttribute_${sa.id}`}
                    values={[emptyAttribute, ...sa.listOptions]}
                    valueAttr="transportationAttributeUUID"
                    labelAttr="name"
                    control={form.control as any}
                  />
                );
              }
            })}
        </>
      )}
    </StyledBox>
  );
};

export { AdditionalAttributes };
